// frontend/.../public/VendorRequalify.tsx
// Public, token-gated re-qualification page.
// Path: /vendor-prequal/requalify?token=<hex>
//
// Flow:
//   1. Validate token via GET /api/vendor-prequal/requalify/:token
//   2. Pre-fill form fields from the returned GT supplier record
//   3. Vendor reviews — Confirm (no changes) or Submit updates, and states the
//      currency of their insurance, attaching Certificates of Currency
//   4. Signature is required on either path
//   5. POST to /api/vendor-prequal/requalify/:token with outcome + updates
//
// Two things this page deliberately does NOT do:
//   * It does not edit bank or remittance details. A renewal link is emailed to
//     an address we cannot independently vouch for, and a changed bank account
//     is the classic payment-redirection fraud. Those changes go through
//     procurement with a callback to a known number.
//   * It does not change anything in GreenTree. The submission is a proposal;
//     procurement reviews it and applies it. The copy below says so, because a
//     vendor who believes their update is live when it is pending will not chase
//     it.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import SignaturePad from '../shared/SignaturePad';
import Alert from '../shared/Alert';
import Button from '../shared/Button';
import { TEXT } from '../shared/Typography';
import { FIELD_INPUT_CLASS, FIELD_INPUT_ERROR_CLASS } from '../shared/FormInput';
import { QueueChip } from '../shared/ApprovalQueue';

const apiBase = import.meta.env.VITE_API_BASE || '';

interface Supplier {
  Code?: string;
  Name?: string;
  Alpha?: string;
  AlternatePayeeName?: string;
  TaxReference?: string;
  Address?: any;
  PayeeAddress?: any;
  UserDefinedFields?: Array<{ Name: string; Type: string; Value: any }>;
}

interface InsuranceField {
  category: string;
  label: string;
  currentExpiry: string;
  currentInsurer: string;
  currentPolicy: string;
}

interface TokenData {
  supplierCode: string;
  supplierName: string;
  supplierEmail: string;
  expiresAt: string;
  supplier: Supplier;
  insuranceFields: InsuranceField[];
  /** Insurance categories this vendor's risk level requires, e.g. ['insurance_gl']. */
  requiredInsurance?: string[];
  qualificationType?: string;
  /**
   * Character limit per field key (`Name`, `insurance_gl:insurer`, ...), as the
   * server enforces it on submit — GreenTree's own where it has stated one.
   * Taken from the server so the page cannot show a different number.
   */
  fieldLimits?: Record<string, number>;
  /** Present when this link was sent because procurement rejected a renewal. */
  resubmission?: {
    reason: string;
    rejectedAt: string | null;
    /** The vendor's previous answers, keyed as `updates` keys them. */
    previousValues: Record<string, string>;
    previousNotes: string;
  } | null;
}

interface InsuranceEntry {
  expiry: string;
  insurer: string;
  policy: string;
  file: File | null;
}

function getToken(): string | null {
  const u = new URL(window.location.href);
  return u.searchParams.get('token');
}

function EditableField({
  label,
  value,
  onChange,
  type = 'text',
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  /**
   * A GreenTree limit, where one is known. The server refuses an over-long
   * value on submit and GreenTree refuses the whole renewal after that, so
   * saying it here is the only place a vendor finds out while it is cheap.
   * The count appears once they are close, rather than nagging from empty.
   */
  maxLength?: number;
}) {
  // Trimmed, as the server counts it.
  const n = value.trim().length;
  const over = !!maxLength && n > maxLength;
  const near = !!maxLength && n > maxLength - 5;
  return (
    <div>
      <label className={`block mb-1 ${TEXT.captionMuted}`}>
        {label}
        {near && (
          <span className={over ? 'ml-1 font-medium text-red-600' : 'ml-1 text-gray-500'}>
            {n}/{maxLength}
          </span>
        )}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        // Not `maxLength` on the input: silently refusing the last keystroke
        // looks like a broken keyboard. Let them type it, then say why.
        aria-invalid={over || undefined}
        className={over ? FIELD_INPUT_ERROR_CLASS : FIELD_INPUT_CLASS}
      />
      {over && (
        <p className={`mt-1 ${TEXT.errorCaption}`}>
          This field accepts at most {maxLength} characters. Please shorten it.
        </p>
      )}
    </div>
  );
}

interface VendorRequalifyProps {
  /**
   * Procurement's "view as vendor": the same page, loaded for a supplier code
   * through the authenticated admin endpoint, with no link behind it and
   * nothing that can be submitted.
   */
  preview?: { supplierCode: string };
}

export default function VendorRequalify({ preview }: VendorRequalifyProps = {}): JSX.Element {
  usePublicPageScroll();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<TokenData | null>(null);

  // Editable form state
  const [form, setForm] = useState({
    Name: '',
    AlternatePayeeName: '',
    TaxReference: '',
    Address1: '',
    Suburb: '',
    Postcode: '',
    State: '',
    Country: '',
    PhoneBH: '',
    Email: '',
    Web: '',
    Contact: '',
  });
  const [insurance, setInsurance] = useState<Record<string, InsuranceEntry>>({});
  const [notes, setNotes] = useState('');

  const [signature, setSignature] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<'confirmed' | 'updated' | null>(null);
  const [submitNotices, setSubmitNotices] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState('');

  const token = useMemo(getToken, []);

  useEffect(() => {
    // Allow body scroll on this standalone page (not when shown in a modal)
    if (preview) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!token && !preview) {
      setError('No token supplied.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        let d: TokenData;
        if (preview) {
          const { data: json } = await axios.get(
            `/api/vendor-prequal/renewals/preview/${encodeURIComponent(preview.supplierCode)}`
          );
          d = json.data;
        } else {
          const res = await fetch(`${apiBase}/api/vendor-prequal/requalify/${token}`);
          const json = await res.json();
          if (!res.ok || !json.success) {
            throw new Error(json.error || `HTTP ${res.status}`);
          }
          d = json.data;
        }
        setData(d);
        const s = d.supplier || {};
        setForm({
          Name: s.Name || '',
          AlternatePayeeName: s.AlternatePayeeName || '',
          TaxReference: s.TaxReference || '',
          Address1: s.Address?.Address1 || '',
          Suburb: s.Address?.Suburb || '',
          Postcode: s.Address?.Postcode || '',
          State: s.Address?.State || '',
          Country: s.Address?.Country || '',
          PhoneBH: s.Address?.PhoneBH || '',
          Email: s.Address?.Email || '',
          Web: s.Address?.Web || '',
          Contact: s.Address?.Contact || '',
        });
        const ins: Record<string, InsuranceEntry> = {};
        (d.insuranceFields || []).forEach(f => {
          ins[f.category] = {
            expiry: f.currentExpiry || '',
            insurer: f.currentInsurer || '',
            policy: f.currentPolicy || '',
            file: null,
          };
        });
        // A link sent after a rejection starts from the vendor's previous
        // answers, so they amend what procurement queried instead of retyping
        // everything. Each still goes through the server's checks again.
        const previous = d.resubmission?.previousValues || {};
        Object.entries(previous).forEach(([key, value]) => {
          const [category, part] = key.split(':');
          if (part && ins[category] && (part === 'expiry' || part === 'insurer' || part === 'policy')) {
            ins[category] = { ...ins[category], [part]: value };
          }
        });
        setForm(f => {
          const next = { ...f };
          (Object.keys(next) as Array<keyof typeof next>).forEach(k => {
            if (typeof previous[k] === 'string') next[k] = previous[k];
          });
          return next;
        });
        if (d.resubmission?.previousNotes) setNotes(d.resubmission.previousNotes);
        setInsurance(ins);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
    // Keyed on the code, not the object: a fresh `preview` literal on every
    // parent render would otherwise refetch GreenTree and reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, preview?.supplierCode]);

  /**
   * Flat, allow-listed key/value pairs — the server validates against its own
   * list, so this only has to send what changed. Insurance uses the composite
   * `<category>:<part>` keys the server expects.
   */
  function buildUpdatesIfChanged(): Record<string, string> | null {
    if (!data?.supplier) return null;
    const s = data.supplier;
    const updates: Record<string, string> = {};

    const addr = s.Address || {};
    (Object.keys(form) as Array<keyof typeof form>).forEach(key => {
      const k = key as string;
      const current =
        k === 'Name' || k === 'AlternatePayeeName' || k === 'TaxReference'
          ? String((s as any)[k] || '')
          : String(addr[k] || '');
      if (form[key] !== current) updates[k] = form[key];
    });

    (data.insuranceFields || []).forEach(f => {
      const entry = insurance[f.category];
      if (!entry) return;
      if (entry.expiry && entry.expiry !== (f.currentExpiry || '')) {
        updates[`${f.category}:expiry`] = entry.expiry;
      }
      if (entry.insurer !== (f.currentInsurer || '')) {
        updates[`${f.category}:insurer`] = entry.insurer;
      }
      if (entry.policy !== (f.currentPolicy || '')) {
        updates[`${f.category}:policy`] = entry.policy;
      }
    });

    return Object.keys(updates).length ? updates : null;
  }

  const hasCertificates = Object.values(insurance).some(e => e.file);
  const limits = data?.fieldLimits || {};

  /** Values over the server's limit — refused on submit, so said before it. */
  function overLimit(): string[] {
    const values: Record<string, string> = { ...form };
    Object.entries(insurance).forEach(([category, e]) => {
      values[`${category}:insurer`] = e.insurer;
      values[`${category}:policy`] = e.policy;
    });
    return Object.entries(values)
      .filter(([k, v]) => limits[k] && String(v || '').trim().length > limits[k])
      .map(([k]) => k);
  }
  const requiredInsurance = new Set(data?.requiredInsurance || []);

  /**
   * The same rule the server enforces (`missingRenewalInsurance`): each
   * insurance this vendor's risk level requires needs a current expiry, an
   * insurer, and a Certificate of Currency attached to this renewal. Checked
   * here so the vendor hears it before signing, not after.
   */
  function insuranceProblems(): string[] {
    // Brisbane's date, as the server checks it (missingRenewalInsurance).
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' });
    const problems: string[] = [];
    (data?.insuranceFields || []).forEach(f => {
      if (!requiredInsurance.has(f.category)) return;
      const e = insurance[f.category];
      if (!e?.expiry) problems.push(`${f.label}: the expiry date is required.`);
      else if (e.expiry < today) problems.push(`${f.label}: that cover has expired — please give the current policy.`);
      if (!e?.insurer?.trim()) problems.push(`${f.label}: the insurer is required.`);
      if (!e?.file) problems.push(`${f.label}: please attach the current Certificate of Currency.`);
    });
    return problems;
  }

  async function submit(outcome: 'confirmed' | 'updated') {
    if (preview) return;
    if (!signature) {
      setSubmitError('Please sign before submitting.');
      return;
    }
    if (overLimit().length) {
      setSubmitError('Some answers are longer than the field allows — they are marked in red. Please shorten them.');
      return;
    }
    const problems = insuranceProblems();
    if (problems.length) {
      setSubmitError(`Current insurance is required to renew. ${problems.join(' ')}`);
      return;
    }
    // "Confirm" sends no field values, so a corrected expiry or a newly typed
    // insurer would be lost — and then refused as missing.
    if (outcome === 'confirmed' && buildUpdatesIfChanged()) {
      setSubmitError('You have changed some details — please use "Submit updates" so they are sent.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      const payload: Record<string, any> = { outcome, signature, notes };
      if (outcome === 'updated') {
        const updates = buildUpdatesIfChanged();
        if (!updates && !hasCertificates) {
          setSubmitError('No changes detected. Use "Confirm (no changes)" instead.');
          setSubmitting(false);
          return;
        }
        if (updates) payload.updates = updates;
      }

      // Always multipart: the same request shape whether or not certificates
      // are attached keeps one code path on the server.
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      Object.entries(insurance).forEach(([category, entry]) => {
        if (entry.file) fd.append(`cert.${category}`, entry.file);
      });

      const res = await fetch(`${apiBase}/api/vendor-prequal/requalify/${token}`, {
        method: 'POST',
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
      setSubmitNotices(Array.isArray(json.rejected) ? json.rejected : []);
      setSubmitted(outcome);
    } catch (err: any) {
      setSubmitError(err?.message || 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (preview && (loading || error)) {
    return (
      <div className="p-6 text-center">
        {loading ? (
          <span className="text-gray-600">Loading the renewal form from GreenTree…</span>
        ) : (
          <Alert variant="error" message={error} />
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-gray-600">Loading your prequalification…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md bg-white p-6 rounded-lg shadow-sm text-center">
          <div className="text-xl font-semibold text-red-700 mb-2">Link unavailable</div>
          <p className="text-gray-700 mb-2">{error}</p>
          <p className={TEXT.helper}>
            If your link has expired or been used, please email{' '}
            <a className="text-blue-700 underline" href="mailto:procurement@grps.com.au">
              procurement@grps.com.au
            </a>{' '}
            to request a new one.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-lg bg-white p-6 rounded-lg shadow-sm text-center">
          <div className="text-xl font-semibold text-green-700 mb-2">Thank you</div>
          <p className="text-gray-700">
            {submitted === 'confirmed'
              ? 'Your confirmation has been received and sent to GRPS Procurement for review.'
              : 'Your updates have been received and sent to GRPS Procurement for review.'}{' '}
            We will be in touch if anything further is needed.
          </p>
          {submitNotices.length > 0 && (
            <div className="mt-4 text-left">
              {submitNotices.map((n, i) => (
                <Alert key={i} variant="warning" message={n} className="mb-2" />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const expiry = data?.expiresAt ? new Date(data.expiresAt) : null;

  return (
    <div className={preview ? 'bg-gray-50' : 'min-h-screen bg-gray-50'}>
      <header className="bg-blue-900 text-white py-4">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-2xl font-semibold">
            GR Production Services — Prequalification Renewal{preview ? ' (preview)' : ''}
          </h1>
          <p className="text-sm text-blue-100">
            Supplier <strong>{data?.supplierCode}</strong> · {data?.supplierName}
            {expiry && (
              <>
                {' '}
                · Link expires {expiry.toLocaleDateString('en-AU')}
              </>
            )}
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {preview && (
          <Alert
            variant="info"
            className="mb-4"
            title="Preview — this is the form the vendor sees"
            message={`Filled from GreenTree for supplier ${preview.supplierCode}${
              data?.qualificationType ? ` (${data.qualificationType})` : ' (no qualification type recorded)'
            }. Nothing here is sent anywhere, and no renewal link has been created.`}
          />
        )}
        {data?.resubmission && (
          <Alert
            variant="warning"
            className="mb-4"
            title="GRPS Procurement could not accept your previous renewal"
            message={`${data.resubmission.reason || 'No reason was recorded.'} — Your previous answers are filled in below. Please correct them, attach your Certificates of Currency again, sign and submit.`}
          />
        )}
        <div className={`bg-white rounded-lg shadow-sm p-4 mb-4 ${TEXT.body}`}>
          Your prequalification is due for renewal. Please review the details below. If
          everything is still correct, click <em>Confirm (no changes)</em>. If anything has
          changed, update the relevant fields and click <em>Submit updates</em>. A drawn
          signature is required either way. Your answers are reviewed by GRPS Procurement
          before they take effect.
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-blue-900 mb-3">Company</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <EditableField
              label="Display Name"
              value={form.Name}
              maxLength={limits.Name}
              onChange={v => setForm(s => ({ ...s, Name: v }))}
            />
            <EditableField
              label="Legal / Alternate Payee Name"
              value={form.AlternatePayeeName}
              maxLength={limits.AlternatePayeeName}
              onChange={v => setForm(s => ({ ...s, AlternatePayeeName: v }))}
            />
            <EditableField
              label="ABN (Tax Reference)"
              value={form.TaxReference}
              maxLength={limits.TaxReference}
              onChange={v => setForm(s => ({ ...s, TaxReference: v }))}
            />
            <EditableField
              label="Primary Contact"
              value={form.Contact}
              maxLength={limits.Contact}
              onChange={v => setForm(s => ({ ...s, Contact: v }))}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-blue-900 mb-3">Address & Contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <EditableField
                label="Street Address"
                value={form.Address1}
              maxLength={limits.Address1}
                onChange={v => setForm(s => ({ ...s, Address1: v }))}
              />
            </div>
            <EditableField
              label="Suburb"
              value={form.Suburb}
              maxLength={limits.Suburb}
              onChange={v => setForm(s => ({ ...s, Suburb: v }))}
            />
            <EditableField
              label="Postcode"
              value={form.Postcode}
              maxLength={limits.Postcode}
              onChange={v => setForm(s => ({ ...s, Postcode: v }))}
            />
            <EditableField
              label="State"
              value={form.State}
              maxLength={limits.State}
              onChange={v => setForm(s => ({ ...s, State: v }))}
            />
            <EditableField
              label="Country"
              value={form.Country}
              maxLength={limits.Country}
              onChange={v => setForm(s => ({ ...s, Country: v }))}
            />
            <EditableField
              label="Phone"
              value={form.PhoneBH}
              maxLength={limits.PhoneBH}
              onChange={v => setForm(s => ({ ...s, PhoneBH: v }))}
            />
            <EditableField
              label="Email"
              type="email"
              value={form.Email}
              maxLength={limits.Email}
              onChange={v => setForm(s => ({ ...s, Email: v }))}
            />
            <EditableField
              label="Website"
              value={form.Web}
              maxLength={limits.Web}
              onChange={v => setForm(s => ({ ...s, Web: v }))}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-blue-900 mb-1">Insurance</h2>
          <p className={`mb-3 ${TEXT.bodyMuted}`}>
            Please confirm the expiry date of each policy and attach a current Certificate of
            Currency. Your prequalification cannot be extended beyond the expiry of your cover.
            {requiredInsurance.size > 0 &&
              ' Policies marked Required must be current, with the insurer named and a certificate attached.'}
          </p>
          {(data?.insuranceFields || []).map(f => {
            const entry = insurance[f.category] || { expiry: '', insurer: '', policy: '', file: null };
            return (
              <div key={f.category} className="border-t pt-4 mt-4 first:border-0 first:mt-0 first:pt-0">
                <div className="font-medium mb-2 text-blue-900">
                  {f.label}
                  {requiredInsurance.has(f.category) && (
                    <span className="ml-2"><QueueChip tone="red">Required</QueueChip></span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <EditableField
                    label="Expiry date"
                    type="date"
                    value={entry.expiry}
                    onChange={v =>
                      setInsurance(s => ({ ...s, [f.category]: { ...entry, expiry: v } }))
                    }
                  />
                  <EditableField
                    label="Insurer"
                    value={entry.insurer}
                    maxLength={limits[`${f.category}:insurer`]}
                    onChange={v =>
                      setInsurance(s => ({ ...s, [f.category]: { ...entry, insurer: v } }))
                    }
                  />
                  <EditableField
                    label="Policy number"
                    value={entry.policy}
                    maxLength={limits[`${f.category}:policy`]}
                    onChange={v =>
                      setInsurance(s => ({ ...s, [f.category]: { ...entry, policy: v } }))
                    }
                  />
                </div>
                <div className="mt-3">
                  <label className={`block mb-1 ${TEXT.captionMuted}`}>
                    Certificate of Currency (PDF or image){requiredInsurance.has(f.category) ? ' — required' : ''}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={e =>
                      setInsurance(s => ({
                        ...s,
                        [f.category]: { ...entry, file: e.target.files?.[0] || null },
                      }))
                    }
                    className="text-sm"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-blue-900 mb-3">Banking</h2>
          <p className={TEXT.bodyMuted}>
            For your security, bank and remittance details cannot be changed through this link.
            If they need updating, please contact{' '}
            <a className="text-blue-700 underline" href="mailto:procurement@grps.com.au">
              procurement@grps.com.au
            </a>{' '}
            — we will confirm the change with you by phone before applying it.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-blue-900 mb-3">Anything else we should know?</h2>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            className={FIELD_INPUT_CLASS}
            placeholder="Optional — any context for the procurement team."
          />
        </div>

        {!preview && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-blue-900 mb-3">Signature</h2>
          <p className={`mb-2 ${TEXT.bodyMuted}`}>
            By signing I acknowledge that the information shown above is accurate (with any
            changes I have made) and I authorise GR Production Services to record this
            acceptance against supplier {data?.supplierCode}.
          </p>
          <SignaturePad onChange={setSignature} />
        </div>
        )}

        {submitError && <Alert variant="error" message={submitError} className="mb-3" />}

        {!preview && (
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" disabled={submitting || !signature} onClick={() => submit('confirmed')}>
            {submitting ? 'Submitting…' : 'Confirm (no changes)'}
          </Button>
          <Button variant="success" disabled={submitting || !signature} onClick={() => submit('updated')}>
            {submitting ? 'Submitting…' : 'Submit updates'}
          </Button>
        </div>
        )}
      </div>
    </div>
  );
}
